/**
 * Visit作成シート
 *
 * 地図のポリラインクリックから呼び出され、周辺Place候補を表示して
 * Visit（滞在記録）を作成するボトムシート。
 *
 * フロー:
 * 1. 自動でGoogle Places APIから周辺施設を検索（マイプレイスも近い順に出す）
 * 2. ユーザーがPlace・マイプレイスを選択（または「場所なし」で作成）
 * 3. POST /overland/visits/create-from-location でVisit作成
 */

import { format } from 'date-fns'
import { Home, Loader2, MapPin, MapPinPlus, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  createPlaceFromSearch,
  createVisitFromLocation,
  getMyPlaces,
  getOverlandVisit,
  type NearbyPlaceResult,
  searchPlacesGoogle,
} from '@/api/services/overland'
import type { MyPlace } from '@/api/types/overland'
import { VisitedPlaceBadge } from '@/components/shared/visited-place-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { formatDistanceMeters, formatTimeJST } from '@/lib/activity-format-utils'
import { calculateDistance } from '@/lib/geo-utils'
import { useOverlandStore } from '@/store/useOverlandStore'

type TabValue = 'nearby' | 'my'

type Props = {
  lat: number
  lng: number
  timestamp: string // ISO 8601
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 追加の副作用フック。store の upsert は内部で行う */
  onCreated?: () => void | Promise<void>
}

export const CreateVisitSheet = ({ lat, lng, timestamp, open, onOpenChange, onCreated }: Props) => {
  const [searchResults, setSearchResults] = useState<NearbyPlaceResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const { isSubmitting: isCreating, run } = useSubmitLock()
  const [keyword, setKeyword] = useState('')
  const [tab, setTab] = useState<TabValue>('nearby')
  const [myPlaces, setMyPlaces] = useState<MyPlace[]>([])
  const [isLoadingMyPlaces, setIsLoadingMyPlaces] = useState(false)
  const [myPlaceQuery, setMyPlaceQuery] = useState('')
  const upsertVisit = useOverlandStore((s) => s.upsertVisit)
  const fetchVisits = useOverlandStore((s) => s.fetchVisits)

  // 作成した visit を store に取り込む
  const handleCreatedVisit = useCallback(
    async (visitId: number) => {
      try {
        const fresh = await getOverlandVisit(visitId)
        upsertVisit(fresh)
      } catch {
        // 単一取得が失敗した場合は当日分を silent fetch にフォールバック
        try {
          const dateStr = format(new Date(timestamp), 'yyyy-MM-dd')
          await fetchVisits(dateStr, undefined, { silent: true })
        } catch {
          // ここも失敗したら諦める（次回ロードで反映）
        }
      }
      await onCreated?.()
    },
    [upsertVisit, fetchVisits, timestamp, onCreated],
  )

  // シート表示時に自動検索
  useEffect(() => {
    if (!open) return
    setSearchResults([])
    setKeyword('')
    const search = async () => {
      setIsSearching(true)
      try {
        const results = await searchPlacesGoogle(lat, lng, undefined, 0.5, 15)
        setSearchResults(results)
      } catch {
        toast.error('場所の検索に失敗しました')
      } finally {
        setIsSearching(false)
      }
    }
    void search()
  }, [open, lat, lng])

  // シート表示時にマイプレイスを読み込み、半径内にあればマイプレイスタブを開く
  useEffect(() => {
    if (!open) return
    setTab('nearby')
    setMyPlaceQuery('')
    const load = async () => {
      setIsLoadingMyPlaces(true)
      try {
        const places = await getMyPlaces()
        setMyPlaces(places)
        const isInside = places.some((p) => calculateDistance(lat, lng, p.latitude, p.longitude) * 1000 <= p.radius)
        if (isInside) setTab('my')
      } catch {
        toast.error('マイプレイスの取得に失敗しました')
      } finally {
        setIsLoadingMyPlaces(false)
      }
    }
    void load()
  }, [open, lat, lng])

  const sortedMyPlaces = useMemo(() => {
    const query = myPlaceQuery.trim()
    return myPlaces
      .filter((p) => !query || p.name.includes(query))
      .map((p) => ({ place: p, distanceM: calculateDistance(lat, lng, p.latitude, p.longitude) * 1000 }))
      .sort((a, b) => a.distanceM - b.distanceM)
  }, [myPlaces, myPlaceQuery, lat, lng])

  // Google Places API 検索
  const handleSearch = useCallback(
    async (searchKeyword?: string) => {
      setIsSearching(true)
      try {
        const results = await searchPlacesGoogle(lat, lng, searchKeyword?.trim() || undefined, 0.5, 15)
        setSearchResults(results)
      } catch {
        toast.error('場所の検索に失敗しました')
      } finally {
        setIsSearching(false)
      }
    },
    [lat, lng],
  )

  // Place選択 → Visit作成
  const handleSelectPlace = useCallback(
    async (result: NearbyPlaceResult) => {
      await run(async () => {
        // 1. 最初にVisitをplace_idなしで作成（Placeが未登録の可能性があるため）
        let visitId: number
        try {
          const visitRes = await createVisitFromLocation({
            latitude: lat,
            longitude: lng,
            timestamp,
          })
          visitId = visitRes.id
        } catch {
          toast.error('Visitの作成に失敗しました')
          return
        }

        // 2. 検索結果からPlaceを作成してVisitに紐付け
        try {
          await createPlaceFromSearch(visitId, result)
          toast.success(`Visit を作成しました: ${result.name}`)
        } catch {
          // Visit は作成済みなので、閉じて取り込んだうえで場所だけ付け直せるようにする
          toast.error('Visitは作成しましたが、場所の紐付けに失敗しました。Visitの編集から場所を選び直してください')
        }
        onOpenChange(false)
        await handleCreatedVisit(visitId)
      })
    },
    [lat, lng, timestamp, onOpenChange, handleCreatedVisit, run],
  )

  // マイプレイス選択 → Visit作成
  const handleSelectMyPlace = useCallback(
    async (myPlace: MyPlace) => {
      await run(async () => {
        try {
          const visitRes = await createVisitFromLocation({
            latitude: lat,
            longitude: lng,
            timestamp,
            place_id: myPlace.id,
          })
          toast.success(`Visit を作成しました: ${myPlace.name}`)
          onOpenChange(false)
          await handleCreatedVisit(visitRes.id)
        } catch {
          toast.error('Visitの作成に失敗しました')
        }
      })
    },
    [lat, lng, timestamp, onOpenChange, handleCreatedVisit, run],
  )

  // 場所なしでVisit作成
  const handleCreateWithoutPlace = useCallback(async () => {
    await run(async () => {
      try {
        const visitRes = await createVisitFromLocation({
          latitude: lat,
          longitude: lng,
          timestamp,
        })
        toast.success('Visit を作成しました（場所なし）')
        onOpenChange(false)
        await handleCreatedVisit(visitRes.id)
      } catch {
        toast.error('Visitの作成に失敗しました')
      }
    })
  }, [lat, lng, timestamp, onOpenChange, handleCreatedVisit, run])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[70vh]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MapPinPlus className="size-5 text-indigo-500" />
            Visitを追加
          </SheetTitle>
          <SheetDescription>
            {formatTimeJST(timestamp)} の地点に滞在記録を作成します。場所を選択してください。
          </SheetDescription>
        </SheetHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v === 'my' ? 'my' : 'nearby')} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="nearby">周辺の場所</TabsTrigger>
            <TabsTrigger value="my">マイプレイス</TabsTrigger>
          </TabsList>

          <TabsContent value="nearby">
            {/* キーワード検索 */}
            <div className="flex gap-2 mt-4">
              <Input
                placeholder="場所名で検索..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleSearch(keyword)
                }}
                disabled={isCreating}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => void handleSearch(keyword)}
                disabled={isSearching || isCreating}
              >
                {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
              </Button>
            </div>

            {/* 検索結果一覧 */}
            <div className="mt-4 space-y-1 max-h-[40vh] overflow-y-auto">
              {isSearching && searchResults.length === 0 && (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin mr-2" />
                  周辺の場所を検索中...
                </div>
              )}

              {!isSearching && searchResults.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">周辺に場所が見つかりませんでした</div>
              )}

              {searchResults.map((result, index) => (
                <button
                  key={`${result.name}-${index}`}
                  type="button"
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-accent transition-colors flex items-start gap-3 disabled:opacity-50"
                  onClick={() => void handleSelectPlace(result)}
                  disabled={isCreating}
                >
                  <div className="size-10 rounded-md bg-muted flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="size-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="min-w-0 font-medium text-sm truncate">{result.name}</div>
                      <VisitedPlaceBadge visitCount={result.visit_count} lastVisitedAt={result.last_visited_at} />
                    </div>
                    {result.address && <div className="text-xs text-muted-foreground truncate">{result.address}</div>}
                    <div className="flex items-center gap-2 mt-0.5">
                      {result.category && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {result.category}
                        </span>
                      )}
                      {result.distance_m != null && (
                        <span className="text-xs text-muted-foreground">
                          {result.distance_m < 1000
                            ? `${Math.round(result.distance_m)}m`
                            : `${(result.distance_m / 1000).toFixed(1)}km`}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="my">
            <Input
              className="mt-4"
              placeholder="名前で絞り込み..."
              value={myPlaceQuery}
              onChange={(e) => setMyPlaceQuery(e.target.value)}
              disabled={isCreating}
            />
            <div className="mt-4 space-y-1 max-h-[40vh] overflow-y-auto">
              {isLoadingMyPlaces && (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin mr-2" />
                  読み込み中...
                </div>
              )}

              {!isLoadingMyPlaces && sortedMyPlaces.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">マイプレイスがありません</div>
              )}

              {sortedMyPlaces.map(({ place, distanceM }) => (
                <button
                  key={place.id}
                  type="button"
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-accent transition-colors flex items-center gap-3 disabled:opacity-50"
                  onClick={() => void handleSelectMyPlace(place)}
                  disabled={isCreating}
                >
                  <div className="size-10 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <Home className="size-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{place.name}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                      <span className={distanceM <= place.radius ? 'text-emerald-600 dark:text-emerald-400' : ''}>
                        {formatDistanceMeters(distanceM)}
                      </span>
                      <span>訪問{place.visit_count}回</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* 場所なしで作成ボタン */}
        <div className="mt-4 pt-3 border-t">
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => void handleCreateWithoutPlace()}
            disabled={isCreating}
          >
            {isCreating ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            場所を指定せずにVisitを作成
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
